// Grids & Gantts — a new value for one field of one copy.
//
// The clone dialog lets a reader CHANGE a field on the copies, per work
// item, not only clear it. A value typed into the wrong shape is a 400
// that reads like a server fault, so the editor here is chosen by the
// field's schema — the same schema the create screen carries — and what it
// hands back is the READ shape of the value (the option with its id, the
// person with their accountId, the date as a day), which ./clone.js turns
// into the create shape exactly as it turns a carried value.
//
//   option            a select over the field's allowed values
//   array of options  a multi-pick over them
//   user / users      a search over the site's people
//   priority          a select over the site's priorities
//   number, date,     the browser's own inputs
//   datetime
//   labels            words, comma-separated
//   anything else     text
//
// A blank means "keep the original's value": the editor never clears —
// clearing is its own deliberate tick in the fields table.
//
// Allowed values come with the create screen when the field is on it; a
// field carried from the original that no screen lists asks the site for
// its options once (cached by field), so it can still be picked from.

import { useEffect, useRef, useState } from 'react';
import { FONT } from './theme';
import MultiPick from './MultiPick';
import { itemsOf } from './clone';

const optionCache = new Map();   // fieldId → Promise<[{ id, value }]>
let prioritiesPromise = null;

const labelOf = (v) => (v == null ? '' : (typeof v === 'object'
  ? (v.displayName || v.value || v.name || v.key || String(v.id || '')) : String(v)));

function optionsFor(api, fieldId) {
  if (!optionCache.has(fieldId)) {
    optionCache.set(fieldId, Promise.resolve()
      .then(() => (api && api.getFieldOptions ? api.getFieldOptions(fieldId) : []))
      .then((r) => (Array.isArray(r) ? r : (r && (r.values || r.options)) || []))
      .catch(() => []));
  }
  return optionCache.get(fieldId);
}
function prioritiesOf(api) {
  if (!prioritiesPromise) {
    prioritiesPromise = Promise.resolve()
      .then(() => (api && api.getPriorities ? api.getPriorities() : []))
      .then((r) => (Array.isArray(r) ? r : []))
      .catch(() => []);
  }
  return prioritiesPromise;
}

// ---- people --------------------------------------------------------------------
function UserPick({ value, onChange, multi, api, ui, S, placeholder }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const list = multi ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  useEffect(() => {
    if (!open) return undefined;
    let on = true;
    const t = setTimeout(() => {
      Promise.resolve().then(() => (api && api.getUsers ? api.getUsers(q) : []))
        .then((r) => { if (on) setHits((Array.isArray(r) ? r : (r && r.values) || []).slice(0, 30)); })
        .catch(() => { if (on) setHits([]); });
    }, 180);
    return () => { on = false; clearTimeout(t); };
  }, [q, open, api]);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  const pick = (u) => {
    const person = { accountId: u.accountId, displayName: u.displayName || u.name || u.accountId };
    if (multi) {
      if (list.some((x) => x.accountId === person.accountId)) return;
      onChange([...list, person]);
    } else { onChange(person); setOpen(false); }
    setQ('');
  };
  const drop = (id) => onChange(multi ? list.filter((x) => x.accountId !== id) : null);
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div onClick={() => setOpen(true)} style={{ ...S.input, padding: '3px 6px', fontSize: 12, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', minHeight: 28, cursor: 'text', borderColor: list.length ? ui.primary : ui.border }}>
        {list.map((u) => (
          <span key={u.accountId} style={{ display: 'inline-flex', gap: 3, alignItems: 'center', background: `${ui.primary}14`, borderRadius: 999, padding: '1px 7px', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {u.displayName || u.accountId}
            <button onClick={(e) => { e.stopPropagation(); drop(u.accountId); }} aria-label="Remove" style={{ border: 'none', background: 'none', cursor: 'pointer', color: ui.muted, padding: 0, fontFamily: FONT, lineHeight: 1 }}>×</button>
          </span>
        ))}
        {multi || !list.length ? (
          <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder={list.length ? '' : (placeholder || 'keep — or find a person…')}
            style={{ border: 'none', background: 'transparent', outline: 'none', flex: '1 1 60px', minWidth: 60, fontSize: 12, fontFamily: FONT, color: ui.fg }} />
        ) : null}
      </div>
      {open ? (
        <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, marginTop: 3, minWidth: 240, maxWidth: 360, maxHeight: 220, overflowY: 'auto', background: ui.card, border: `1px solid ${ui.border}`, borderRadius: 8, boxShadow: ui.shadowMd, padding: 4 }}>
          {hits.length ? hits.map((u) => (
            <div key={u.accountId} onClick={() => pick(u)} style={{ padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {u.displayName || u.accountId}{u.emailAddress ? <span style={{ color: ui.muted, fontSize: 11 }}> · {u.emailAddress}</span> : null}
            </div>
          )) : <div style={{ ...S.tiny, padding: '4px 8px' }}>{q ? 'nobody found' : 'type a name…'}</div>}
        </div>
      ) : null}
    </div>
  );
}

// ---- the editor -----------------------------------------------------------------
export default function CloneValue({ fieldId, def, value, onChange, api, ui, S, placeholder = 'keep' }) {
  const schema = (def && (def.schema || def)) || {};
  const type = schema.type || '';
  // What a list holds, worked out when the definition leaves it out — a
  // multi-select otherwise fell through to the text box at the bottom.
  const items = type === 'array' ? itemsOf({ ...schema, allowedValues: def && def.allowedValues }) : (schema.items || '');
  const system = schema.system || fieldId;
  const [fetched, setFetched] = useState(null);
  const listed = def && Array.isArray(def.allowedValues) && def.allowedValues.length ? def.allowedValues : null;
  const wantsOptions = !listed && (type === 'option' || (type === 'array' && items === 'option') || type === 'priority' || system === 'priority');
  useEffect(() => {
    if (!wantsOptions) return undefined;
    let on = true;
    (type === 'priority' || system === 'priority' ? prioritiesOf(api) : optionsFor(api, fieldId))
      .then((list) => { if (on) setFetched(list); });
    return () => { on = false; };
  }, [wantsOptions, api, fieldId, type, system]);
  const allowed = listed || fetched || [];
  const small = { ...S.input, padding: '4px 6px', fontSize: 12, minHeight: 28 };
  const keyOfOpt = (o) => String((o && (o.id != null ? o.id : (o.value != null ? o.value : o.name))) || '');

  if (type === 'user' || system === 'assignee' || system === 'reporter') {
    return <UserPick value={value || null} onChange={onChange} multi={false} api={api} ui={ui} S={S} />;
  }
  if (type === 'array' && items === 'user') {
    return <UserPick value={Array.isArray(value) ? value : []} onChange={onChange} multi api={api} ui={ui} S={S} />;
  }
  if (type === 'option' || type === 'priority' || system === 'priority') {
    const cur = value ? keyOfOpt(value) : '';
    return (
      <select value={cur} onChange={(e) => { const hit = allowed.find((o) => keyOfOpt(o) === e.target.value); onChange(hit || null); }}
        style={{ ...small, cursor: 'pointer', borderColor: cur ? ui.primary : ui.border, fontWeight: cur ? 700 : 400 }}>
        <option value="">{allowed.length ? `— ${placeholder} —` : (fetched === null && wantsOptions ? 'reading the options…' : '— no options on this site —')}</option>
        {allowed.map((o) => <option key={keyOfOpt(o)} value={keyOfOpt(o)}>{labelOf(o)}</option>)}
      </select>
    );
  }
  if (type === 'array' && items === 'option') {
    const cur = (Array.isArray(value) ? value : []).map(keyOfOpt);
    return (
      <MultiPick value={cur} options={allowed.map((o) => ({ value: keyOfOpt(o), label: labelOf(o) }))} ui={ui} S={S} width="100%"
        placeholder={`— ${placeholder} —`}
        onChange={(ids) => onChange(ids.map((id) => allowed.find((o) => keyOfOpt(o) === String(id))).filter(Boolean))} />
    );
  }
  if (type === 'array' && items === 'string') {
    return (
      <input defaultValue={(Array.isArray(value) ? value : []).join(', ')} placeholder={`${placeholder} — or words, comma-separated`}
        onBlur={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={{ ...small, borderColor: Array.isArray(value) && value.length ? ui.primary : ui.border }} />
    );
  }
  if (type === 'number') {
    return (
      <input type="number" step="any" defaultValue={value == null || value === '' ? '' : value} placeholder={placeholder}
        onBlur={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={{ ...small, borderColor: value != null && value !== '' ? ui.primary : ui.border }} />
    );
  }
  // A NATIVE DATE INPUT CANNOT SHOW A PLACEHOLDER, so it cannot say on its
  // own that a blank keeps the original's day. The kept value is written
  // beside it, and a set value gets a × that puts the blank — and the
  // original — back. Each row's date is its own: one copy can be given a
  // day while the rest keep theirs.
  if (type === 'date' || type === 'datetime') {
    const isDate = type === 'date';
    const shown = value ? String(value).slice(0, isDate ? 10 : 16) : '';
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', minWidth: 0 }}>
        <input type={isDate ? 'date' : 'datetime-local'} value={shown}
          onChange={(e) => onChange(e.target.value ? (isDate ? e.target.value : new Date(e.target.value).toISOString()) : null)}
          title={value ? 'The copy gets this day — × keeps the original’s instead' : placeholder}
          style={{ ...small, width: 'auto', flex: '0 1 auto', minWidth: 0, borderColor: value ? ui.primary : ui.border, fontWeight: value ? 700 : 400 }} />
        {value ? (
          <button onClick={() => onChange(null)} title="Keep the original’s value instead" aria-label="Keep the original’s value"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: ui.muted, padding: '0 2px', fontFamily: FONT, fontSize: 14, lineHeight: 1, flex: '0 0 auto' }}>×</button>
        ) : (
          <span style={{ fontSize: 11, color: ui.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} title={placeholder}>{placeholder}</span>
        )}
      </span>
    );
  }
  // Text, and everything the site names that no editor above fits: typed as
  // it is, and the create's own answer says whether it took.
  return (
    <input defaultValue={value == null ? '' : (typeof value === 'object' ? labelOf(value) : String(value))} placeholder={placeholder}
      onBlur={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      style={{ ...small, borderColor: value != null && value !== '' ? ui.primary : ui.border }} />
  );
}
